import { defineStore } from 'pinia'
import type { CartItem } from '~/models/cartItem'
import type { Product, Variant } from '~/models/product'
import type { Customer } from '~/models/customer'
import { useProductLocalStorage } from '~/composables/useProductLocalStorage'
import { useCustomerLocalStorage } from '~/composables/useCustomerLocalStorage'
import { useShippingLocalStorage } from '~/composables/useShippingLocalStorage'
import { useTaxRates } from "~/composables/useTaxRates"

export const useCheckoutStore = defineStore('checkoutCart', {
  state: () => ({
    items: [] as CartItem[],
    customer: {} as Customer,
    shippingAddress: {} as Customer,
    cartCount: 0,
    cartTotal: 0,
    taxRate: 0 as number,  
  }),

  actions: {
    async updateCustomer(partial: Partial<Customer>) {

      this.customer = { ...this.customer, ...partial }
      const customerStorage = useCustomerLocalStorage()
      customerStorage.saveCustomerToLocalStorage(this.customer)
      await this.fetchAndSetTaxRate()
    },

    updateShippingAddress(partial: Partial<Customer>) {
      this.shippingAddress = { ...this.shippingAddress, ...partial }
      const shippingStorage = useShippingLocalStorage()
      shippingStorage.saveShippingToLocalStorage(this.shippingAddress)
    },

    async initializeCart() {
      const productStorage = useProductLocalStorage()
      const customerStorage = useCustomerLocalStorage()
      const shippingStorage = useShippingLocalStorage()

      this.items = productStorage.getCartFromLocalStorage()
      this.customer = customerStorage.getCustomerFromLocalStorage() || {} as Customer
      this.shippingAddress = shippingStorage.getShippingFromLocalStorage() || {} as Customer

      this.setCartCount()
      this.calculateTotal()
      await this.fetchAndSetTaxRate()
    },
    async fetchAndSetTaxRate() {
      const { getCombinedRateByState } = useTaxRates()
      if (this.customer.state) {
        const rate = await getCombinedRateByState(this.customer.state)
        this.taxRate = rate ?? 0
      } else {
        this.taxRate = 0
      }
    },

    setCartCount() {
      this.cartCount = this.items.reduce((count, item) => count + item.quantity, 0)
    },

    addToCart(product: Product, quantity: number = 1, variant?: Variant | null) {
      const productStorage = useProductLocalStorage()
      const existingItem = this.items.find(item => item.id === product.id)

      if (existingItem) {
        existingItem.quantity = quantity
        existingItem.variant = variant ? variant : undefined
      }
      else {
        const cartItem: CartItem = { id: product.id, product, quantity, variant: variant ?? undefined }
        this.items.push(cartItem)
      }

      this.calculateTotal()
      productStorage.saveCartToLocalStorage(this.items)
      this.setCartCount()
    },

    removeFromCart(productId: string) {
      const productStorage = useProductLocalStorage()
      const index = this.items.findIndex(item => item.id === productId)

      if (index !== -1) {
        this.items.splice(index, 1)
      }

      productStorage.removeItemFromLocalStorage(productId)
      this.calculateTotal()
      this.setCartCount()
    },

    clearCart() {
      const productStorage = useProductLocalStorage()
      const customerStorage = useCustomerLocalStorage()
      const shippingStorage = useShippingLocalStorage()

      this.items = []
      this.cartTotal = 0
      this.customer = {} as Customer
      this.shippingAddress = {} as Customer

      productStorage.clearCart()
      customerStorage.clearCustomer()
      shippingStorage.clearShipping()

      this.setCartCount()
    },

    calculateTotal() {
      this.cartTotal = this.items.reduce(
        (total, item) => total + item.product.price * item.quantity,
        0,
      )
    },
  },

  getters: {
    getCustomer: (state): Customer => state.customer,
    getShippingAddress: (state): Customer => state.shippingAddress,
    getCartItems: (state): CartItem[] => state.items,
    taxAmount: (state) => {
    if (!state.taxRate) return 0
      return state.cartTotal * state.taxRate
    },
    grandTotal: (state) => state.cartTotal + (state.taxRate ? state.cartTotal * state.taxRate : 0),

    isCustomerComplete: (state): boolean => {
      const { firstName, lastName, email, phone, address, city, state: region, zip } = state.customer
      return !!(firstName && lastName && email && phone && address && city && region && zip)
    },

    isShippingComplete: (state): boolean => {
      const { firstName, lastName, email, phone, address, city, state: region, zip } = state.shippingAddress
      return !!(firstName && lastName && email && phone && address && city && region && zip)
    },
  },
})
